import { ServiceStoreConfig } from "./types/ServiceStoreTypes";

// NOTE: the @intrasync/accounting entity stores (accounting-*) are NOT spread
// here. installAccountingHub (in createRootStore) eager-mounts them
// synchronously so they exist in mobx-react's first-render Provider snapshot;
// delivering them again through this (deferred) serviceStores lane would
// re-mount them after first render and overwrite the eager instances.
// ⚠️ REQUIRES @intrasync/accounting >= 0.0.30 (the eager-mount installer).
// On 0.0.29 the installer does NOT mount stores — merging this removal before
// bumping the pin leaves the hub (and the native invoices/bills/payments
// screens that alias accounting stores) with ZERO entity stores. Land this
// change and the 0.0.30 pin bump in the SAME commit/deploy.

// Import categorized service stores
import { aiStores } from "./serviceStores/ai.stores";
import { castStoneStores } from "./serviceStores/castStone.stores";
import { communicationStores } from "./serviceStores/communication.stores";
import { crmStores } from "./serviceStores/crm.stores";
import { departmentStores } from "./serviceStores/department.stores";
import { dispatchStores } from "./serviceStores/dispatch.stores";
import { equipmentStores } from "./serviceStores/equipment.stores";
import { fieldStores } from "./serviceStores/field.stores";
import { financialStores } from "./serviceStores/financial.stores";
import { hardwareStores } from "./serviceStores/hardware.stores";
import { jobStores } from "./serviceStores/job.stores";
import { manufacturingStores } from "./serviceStores/manufacturing.stores";
import { projectStores } from "./serviceStores/project.stores";
import { notificationStores } from "./serviceStores/notification.stores";
import { installStores } from "./serviceStores/install.stores";
import { pmStores } from "./serviceStores/pm.stores";
import { shippingStores } from "./serviceStores/shipping.stores";
import { purchasingStores } from "./serviceStores/purchasing.stores";
import { organizationDataStores } from "./serviceStores/organization-data.stores";
import { panelsStores } from "./serviceStores/panels.stores";
import { pdfEditorStores } from "./serviceStores/pdf-editor.stores";
import { qualityStores } from "./serviceStores/quality.stores";
import { schedulingStores } from "./serviceStores/scheduling.stores";
import { systemStores } from "./serviceStores/system.stores";
import { userStores } from "./serviceStores/user.stores";
import { yardStores } from "./serviceStores/yard.stores";
import { uncategorizedStores } from "./serviceStores/uncategorized.stores";

/**
 * Main Service Store Configuration
 * Combines all categorized service stores into a single array
 *
 * Categories are organized based on the api-types directory structure:
 * - AI: AI services (OpenAI, etc.)
 * - Communication: Chat, support tickets, RFI, submittals
 * - CRM: Accounts, contacts, opportunities, orders
 * - Department: Organization structure
 * - Dispatch: Load and trailer management
 * - Field: Field operations, issues, warranty claims
 * - Financial: Invoicing, billing, change orders, PO approvals
 * - Hardware: Hardware orders and details
 * - Job: Project and job management
 * - Manufacturing: BOM, materials, products, QC, NCR
 * - Organization Data: List tracker
 * - Panels: Panel production, additional casting, remediation
 * - PDF Editor: Document management
 * - Scheduling: Schedule generation, holidays
 * - System: API services, Azure blob storage
 * - User: User management, certifications, training
 * - Yard: Yard inventory and management
 * - Uncategorized: Services without api-types yet
 */
const serviceStores: ServiceStoreConfig[] = [
  // AI Services
  ...aiStores,

  // Cast Stone Module Services (@intrasync/cast-stone)
  ...castStoneStores,

  // Communication Services
  ...communicationStores,

  // CRM Services
  ...crmStores,

  // Department Services
  ...departmentStores,

  // Dispatch Services
  ...dispatchStores,

  // Equipment Services (calibrations, daily inspection, and the Plant
  // Maintenance BE-11 stores: equipment-pm-schedules + equipment-maintenance-
  // entries). production-equipment itself is registered under Manufacturing.
  ...equipmentStores,

  // Field Services
  ...fieldStores,

  // Financial Services
  ...financialStores,

  // Hardware Services
  ...hardwareStores,

  // Job Services
  ...jobStores,

  // Project Services
  ...projectStores,

  // Manufacturing Services
  ...manufacturingStores,

  // Organization Data Services
  ...organizationDataStores,

  // Panel Services
  ...panelsStores,

  // PDF Editor Services
  ...pdfEditorStores,

  // Quality Services (QC module — was previously orphan; wired 2026-06-01)
  ...qualityStores,

  // Scheduling Services
  ...schedulingStores,

  // System Services
  ...systemStores,

  // Notification Center (Slice 1) — system-domain stores for the in-app inbox.
  ...notificationStores,

  // Install Drawings Upload — per-project install artifact metadata.
  ...installStores,
  // PM tracker — Premier PM workbook (2026-10-19).
  ...pmStores,

  // Shipping module — shipments, BoLs, log entries, shipment_pieces link table.
  ...shippingStores,

  // Purchasing module — starter Purchasing module (orders, attachments,
  // scratchpads, vendor notes). Backs the /purchasing route.
  ...purchasingStores,

  // User Services
  ...userStores,

  // Yard Services
  ...yardStores,

  // Uncategorized Services (no api-types directory yet)
  ...uncategorizedStores,
];

export default serviceStores;